import React from "react";
import { StickerDef } from "./StickerDesigns";

// Hand-tuned hero stickers. Each one is its own SVG composition — not the
// generic "text-in-a-shape" pattern. They override the default 5 variants
// for specific tags, and the moment stickers below are always available.
//
// Naming convention for keys: lowercase tag name as it appears in clip.tags
// (we normalize on lookup).

const ELECTRIC_BLUE = "#0affff";
const NEON_PINK = "#ff6ad5";
const HIGHLIGHT_GREEN = "#8FBC8F";
const HIGHLIGHT_DANGER = "#CD5C5C";
const BEER_YELLOW = "#FFC44D";
const BLACK = "#000000";
const WHITE = "#FFFFFF";

const VB = 512;

// ═══════════════════════════════════════════════════════════════════
// MOMENT STICKERS — always-available, not tied to a specific tag
// ═══════════════════════════════════════════════════════════════════

// JUST LANDED — burst-stamp moment sticker for the "I did this" flex.
// Aggressive 20-point star with stacked typography and a heavy black stop.
function JustLanded() {
    return (
        <>
            <defs>
                <linearGradient id="just-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ELECTRIC_BLUE} />
                    <stop offset="100%" stopColor={NEON_PINK} />
                </linearGradient>
            </defs>
            <g transform="translate(256 256)" style={{ filter: "drop-shadow(0 10px 0 #000) drop-shadow(0 0 20px rgba(10,255,255,0.5))" }}>
                <polygon
                    fill="url(#just-grad)"
                    stroke={WHITE}
                    strokeWidth={6}
                    points="0,-220 22,-110 130,-150 70,-60 200,-50 80,0 200,50 70,60 130,150 22,110 0,220 -22,110 -130,150 -70,60 -200,50 -80,0 -200,-50 -70,-60 -130,-150 -22,-110"
                />
            </g>
            <g transform="rotate(-6 256 256)">
                <text x={256} y={222}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={100} textAnchor="middle" letterSpacing={2}
                    stroke={BLACK} strokeWidth={8} paintOrder="stroke fill" fill={WHITE}>
                    JUST
                </text>
                <text x={256} y={322}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={140} textAnchor="middle" letterSpacing={0}
                    stroke={BLACK} strokeWidth={10} paintOrder="stroke fill" fill={WHITE}>
                    LANDED
                </text>
            </g>
        </>
    );
}

// TOW ROPE PASS — ski-lift-pass styled identity sticker. Notched ticket shape,
// black header bar with ECLIPTIC, big handle, season + access tier in fine print.
// User passes the actual username at render time via a wrapper.
function TowRopePass(username: string) {
    const handle = username.startsWith("@") ? username : `@${username}`;
    return (
        <>
            <g transform="rotate(-4 256 256)" style={{ filter: "drop-shadow(0 10px 0 #000)" }}>
                {/* ticket body */}
                <rect x={50} y={150} width={412} height={212} rx={10}
                    fill={WHITE} stroke={BLACK} strokeWidth={4} />
                {/* perforated edge effect (4 circles into the body) */}
                <circle cx={50} cy={256} r={12} fill={BLACK} />
                <circle cx={462} cy={256} r={12} fill={BLACK} />
                {/* black header bar */}
                <rect x={50} y={150} width={412} height={48} fill={BLACK} />
                <text x={70} y={184}
                    fontFamily="'Mukta Mahee', system-ui, sans-serif"
                    fontWeight={700} fontSize={26} letterSpacing={6} fill={ELECTRIC_BLUE}>
                    ECLIPTIC · TOW ROPE PASS
                </text>
                {/* the handle in big display */}
                <text x={70} y={272}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={68} letterSpacing={-1} fill={BLACK}>
                    {handle.toUpperCase()}
                </text>
                {/* fine print */}
                <text x={70} y={328}
                    fontFamily="'Mukta Mahee', system-ui, sans-serif"
                    fontWeight={700} fontSize={20} letterSpacing={3} fill={BLACK} opacity={0.7}>
                    SEASON · UNLIMITED LAPS · ALL TERRAIN
                </text>
                {/* hole punch on right (where the lanyard goes) */}
                <circle cx={428} cy={195} r={10} fill={BLACK} />
                {/* tow rope detail running along the bottom */}
                <path
                    d="M 60 354 Q 100 348 140 354 T 220 354 T 300 354 T 380 354 T 452 354"
                    stroke={NEON_PINK} strokeWidth={4} fill="none" strokeLinecap="round"
                    style={{ filter: `drop-shadow(0 0 8px ${NEON_PINK})` }}
                />
            </g>
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// FLAVOR HEROES — the funny sauce tags
// ═══════════════════════════════════════════════════════════════════

// BEER IN HAND — beer can silhouette with foam, the most viral flavor tag.
function BeerInHand() {
    return (
        <>
            {/* Beer can */}
            <g transform="translate(120 130) rotate(-12)" style={{ filter: "drop-shadow(0 8px 0 #000)" }}>
                {/* foam */}
                <ellipse cx={50} cy={4} rx={56} ry={14} fill={WHITE} />
                <circle cx={20} cy={-8} r={12} fill={WHITE} />
                <circle cx={48} cy={-14} r={14} fill={WHITE} />
                <circle cx={78} cy={-10} r={11} fill={WHITE} />
                {/* can */}
                <rect x={-6} y={4} width={112} height={210} rx={4}
                    fill={BEER_YELLOW} stroke={BLACK} strokeWidth={6} />
                {/* label band */}
                <rect x={-6} y={64} width={112} height={80} fill={BLACK} />
                <text x={50} y={102}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={36} textAnchor="middle" fill={BEER_YELLOW}>
                    BISK
                </text>
                <text x={50} y={130}
                    fontFamily="'Mukta Mahee', system-ui, sans-serif"
                    fontWeight={700} fontSize={14} textAnchor="middle" fill={BEER_YELLOW} letterSpacing={2}>
                    PARK LAGER
                </text>
                {/* highlight */}
                <rect x={-3} y={6} width={6} height={204} fill={WHITE} opacity={0.35} rx={2} />
            </g>
            {/* Text */}
            <g transform="translate(0 380) rotate(-3 256 0)">
                <text x={256} y={20}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={68} textAnchor="middle" letterSpacing={2}
                    stroke={BLACK} strokeWidth={10} paintOrder="stroke fill" fill={BEER_YELLOW}
                    style={{ filter: `drop-shadow(0 4px 0 #000)` }}>
                    BEER IN HAND
                </text>
            </g>
        </>
    );
}

// HALL OF MEAT — slam/wipeout stamp. Cracked-glass burst, red on black.
function HallOfMeat() {
    return (
        <>
            <defs>
                <linearGradient id="hom-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={HIGHLIGHT_DANGER} />
                    <stop offset="100%" stopColor="#7a1f1f" />
                </linearGradient>
            </defs>
            {/* jagged starburst */}
            <g transform="translate(256 256)" style={{ filter: "drop-shadow(0 10px 0 #000)" }}>
                <polygon
                    fill="url(#hom-grad)"
                    stroke={BLACK}
                    strokeWidth={6}
                    points="0,-230 30,-160 100,-180 75,-100 180,-90 110,-40 220,10 110,30 150,120 65,90 50,200 0,140 -50,200 -65,90 -150,120 -110,30 -220,10 -110,-40 -180,-90 -75,-100 -100,-180 -30,-160"
                />
            </g>
            {/* fissure cracks */}
            <g stroke={WHITE} strokeWidth={2} fill="none" opacity={0.6}>
                <path d="M 256 80 L 240 180 L 260 200 L 240 240" />
                <path d="M 256 432 L 270 332 L 250 312 L 270 272" />
                <path d="M 60 256 L 160 240 L 180 260 L 220 240" />
                <path d="M 452 256 L 352 270 L 332 250 L 292 270" />
            </g>
            {/* text */}
            <g transform="rotate(-8 256 256)">
                <text x={256} y={232}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={64} textAnchor="middle" letterSpacing={4}
                    stroke={BLACK} strokeWidth={6} paintOrder="stroke fill" fill={WHITE}>
                    HALL OF
                </text>
                <text x={256} y={322}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={130} textAnchor="middle" letterSpacing={2}
                    stroke={BLACK} strokeWidth={10} paintOrder="stroke fill" fill={WHITE}>
                    MEAT
                </text>
            </g>
        </>
    );
}

// HAND DRAG — minimalist hand-silhouette + curved drag-line + label.
function HandDrag() {
    return (
        <>
            {/* the drag arc */}
            <path
                d="M 100 360 Q 256 420 412 280"
                stroke={ELECTRIC_BLUE} strokeWidth={10} fill="none" strokeLinecap="round" strokeDasharray="4 14"
                opacity={0.85}
                style={{ filter: `drop-shadow(0 0 12px ${ELECTRIC_BLUE})` }}
            />
            {/* speed lines off the hand */}
            <g stroke={ELECTRIC_BLUE} strokeWidth={4} strokeLinecap="round" opacity={0.7}>
                <line x1={70} y1={340} x2={120} y2={350} />
                <line x1={50} y1={370} x2={110} y2={376} />
                <line x1={70} y1={400} x2={120} y2={400} />
            </g>
            {/* hand glyph — stylized palm */}
            <g transform="translate(120 360)" style={{ filter: "drop-shadow(0 4px 0 #000)" }}>
                <path
                    d="M 0 0
                       C -6 -40, 6 -54, 16 -50
                       L 16 -10
                       L 24 -10
                       L 24 -56
                       C 24 -62, 34 -62, 34 -56
                       L 34 -10
                       L 42 -10
                       L 42 -50
                       C 42 -56, 52 -56, 52 -50
                       L 52 -10
                       L 60 -10
                       L 60 -36
                       C 60 -42, 70 -42, 70 -36
                       L 70 4
                       C 70 30, 50 40, 30 40
                       C 14 40, 0 30, 0 0 Z"
                    fill={WHITE} stroke={BLACK} strokeWidth={4}
                />
            </g>
            {/* text */}
            <g transform="rotate(-4 256 156)">
                <text x={256} y={130}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={86} textAnchor="middle" letterSpacing={4}
                    stroke={BLACK} strokeWidth={10} paintOrder="stroke fill" fill={ELECTRIC_BLUE}
                    style={{ filter: `drop-shadow(0 6px 0 #000)` }}>
                    HAND DRAG
                </text>
            </g>
        </>
    );
}

// PARKASAURUS — dino silhouette in a beanie. Cartoon energy.
function Parkasaurus() {
    return (
        <>
            {/* dino silhouette */}
            <g transform="translate(180 200) rotate(-4)" style={{ filter: "drop-shadow(0 8px 0 #000)" }}>
                {/* body (gourd-ish shape) */}
                <path
                    d="M 0 60
                       C -20 60, -40 80, -40 110
                       C -40 150, -10 170, 30 170
                       L 90 170
                       L 130 170
                       C 160 170, 170 150, 170 130
                       L 170 100
                       C 170 70, 145 50, 110 50
                       L 100 50
                       L 100 30
                       C 100 0, 80 -20, 50 -20
                       C 30 -20, 10 0, 5 25
                       C 0 40, -5 50, 0 60 Z"
                    fill={HIGHLIGHT_GREEN} stroke={BLACK} strokeWidth={5}
                />
                {/* spine spikes */}
                <polygon points="20,-15 30,-30 40,-15" fill={HIGHLIGHT_GREEN} stroke={BLACK} strokeWidth={4} />
                <polygon points="55,-20 65,-38 75,-20" fill={HIGHLIGHT_GREEN} stroke={BLACK} strokeWidth={4} />
                {/* eye */}
                <circle cx={40} cy={15} r={5} fill={BLACK} />
                {/* beanie */}
                <ellipse cx={45} cy={-15} rx={45} ry={10} fill={NEON_PINK} stroke={BLACK} strokeWidth={4} />
                <rect x={0} y={-30} width={90} height={18} rx={4} fill={NEON_PINK} stroke={BLACK} strokeWidth={4} />
                <circle cx={45} cy={-44} r={12} fill={WHITE} stroke={BLACK} strokeWidth={4} />
                {/* legs */}
                <rect x={20} y={160} width={20} height={32} fill={HIGHLIGHT_GREEN} stroke={BLACK} strokeWidth={5} />
                <rect x={110} y={160} width={20} height={32} fill={HIGHLIGHT_GREEN} stroke={BLACK} strokeWidth={5} />
                {/* tail */}
                <path d="M 170 110 Q 200 100 220 120 L 215 130 Q 195 120 170 130 Z" fill={HIGHLIGHT_GREEN} stroke={BLACK} strokeWidth={5} />
            </g>
            {/* text */}
            <g transform="rotate(-3 256 440)">
                <text x={256} y={460}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={62} textAnchor="middle" letterSpacing={2}
                    stroke={BLACK} strokeWidth={8} paintOrder="stroke fill" fill={HIGHLIGHT_GREEN}
                    style={{ filter: `drop-shadow(0 4px 0 #000)` }}>
                    PARKASAURUS
                </text>
            </g>
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════
// FAMILY EXEMPLARS — one hero per family showing the pattern
// ═══════════════════════════════════════════════════════════════════

// Generic degree hero — parameterized so 180/270/360/540/720/900/1080/1260
// all share the same hand-tuned spin-arc + motion-line + gradient treatment.
// `wraps` controls how many partial loops the spin arc draws; the number itself
// is rendered as-is.
function makeDegreeHero(label: string, wraps: number) {
    return function DegreeHero() {
        // Generate `wraps` concentric arcs that fan out, suggesting multiple
        // rotations rather than a single 360.
        const arcs: { d: string; opacity: number }[] = [];
        const innerR = 96;
        for (let i = 0; i < wraps; i++) {
            const r = innerR + i * 12;
            arcs.push({
                d: `M ${256 - r} ${256} A ${r} ${r} 0 1 1 ${256 + r} ${256}`,
                opacity: 0.45 + (i / Math.max(wraps - 1, 1)) * 0.55,
            });
        }
        return (
            <>
                <defs>
                    <linearGradient id={`deg-grad-${label}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={ELECTRIC_BLUE} />
                        <stop offset="100%" stopColor={NEON_PINK} />
                    </linearGradient>
                    <linearGradient id={`deg-arc-${label}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={ELECTRIC_BLUE} stopOpacity={0.1} />
                        <stop offset="50%" stopColor={ELECTRIC_BLUE} stopOpacity={1} />
                        <stop offset="100%" stopColor={NEON_PINK} stopOpacity={1} />
                    </linearGradient>
                </defs>
                {/* motion lines */}
                <g stroke={ELECTRIC_BLUE} strokeLinecap="round" opacity={0.6}>
                    <line x1={40} y1={200} x2={100} y2={195} strokeWidth={6} />
                    <line x1={30} y1={240} x2={110} y2={240} strokeWidth={6} />
                    <line x1={40} y1={280} x2={100} y2={285} strokeWidth={6} />
                </g>
                <g stroke={NEON_PINK} strokeLinecap="round" opacity={0.5}>
                    <line x1={20} y1={320} x2={80} y2={330} strokeWidth={4} />
                    <line x1={25} y1={170} x2={85} y2={160} strokeWidth={4} />
                </g>
                {/* spin arc(s) — one per wrap */}
                {arcs.map((a, i) => (
                    <path key={i} d={a.d}
                        fill="none" stroke={`url(#deg-arc-${label})`}
                        strokeWidth={10 - i * 1.5} strokeLinecap="round" opacity={a.opacity} />
                ))}
                <polygon points={`${256 + innerR + (wraps - 1) * 12},256 ${256 + innerR + (wraps - 1) * 12 - 12},236 ${256 + innerR + (wraps - 1) * 12 + 20},256 ${256 + innerR + (wraps - 1) * 12 - 12},276`}
                    fill={NEON_PINK} style={{ filter: `drop-shadow(0 0 8px ${NEON_PINK})` }} />
                {/* the number */}
                <g transform="translate(0,8) rotate(-3 256 256)">
                    <text x={256} y={298}
                        fontFamily="Anton, 'Mukta Mahee', sans-serif"
                        fontWeight={700} fontSize={220} textAnchor="middle" letterSpacing={-2}
                        stroke={BLACK} strokeWidth={14} paintOrder="stroke fill" fill={`url(#deg-grad-${label})`}
                        style={{ filter: `drop-shadow(0 8px 0 #000) drop-shadow(0 0 22px ${ELECTRIC_BLUE})` }}>
                        {label}
                    </text>
                </g>
                <text x={384} y={148}
                    fontFamily="'Mukta Mahee', system-ui, sans-serif"
                    fontWeight={700} fontSize={22} letterSpacing={4} fill={ELECTRIC_BLUE} opacity={0.85}
                    transform="rotate(-3 384 148)">
                    ° DEG
                </text>
            </>
        );
    };
}

const Degree180  = makeDegreeHero("180", 1);
const Degree270  = makeDegreeHero("270", 1);
const Degree360  = makeDegreeHero("360", 1);
const Degree540  = makeDegreeHero("540", 2);
const Degree720  = makeDegreeHero("720", 2);
const Degree900  = makeDegreeHero("900", 3);
const Degree1080 = makeDegreeHero("1080", 3);

// CORK hero — tilted axis ellipse + rotation arrows + text.
function AxisCork() {
    return (
        <>
            <defs>
                <linearGradient id="cork-grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={HIGHLIGHT_GREEN} />
                    <stop offset="100%" stopColor={ELECTRIC_BLUE} />
                </linearGradient>
            </defs>
            {/* tilted axis ellipse */}
            <g transform="translate(256 256) rotate(-30)">
                <ellipse cx={0} cy={0} rx={180} ry={56}
                    fill="none" stroke={HIGHLIGHT_GREEN} strokeWidth={6} opacity={0.85}
                    style={{ filter: `drop-shadow(0 0 12px ${HIGHLIGHT_GREEN})` }} />
                {/* arrows on the ellipse */}
                <polygon points="180,0 170,-12 198,0 170,12" fill={HIGHLIGHT_GREEN} />
                <polygon points="-180,0 -170,12 -198,0 -170,-12" fill={HIGHLIGHT_GREEN} />
                {/* dashed inner ellipse */}
                <ellipse cx={0} cy={0} rx={130} ry={36}
                    fill="none" stroke={ELECTRIC_BLUE} strokeWidth={2} strokeDasharray="6 10" opacity={0.5} />
            </g>
            {/* text */}
            <g transform="rotate(-4 256 256)">
                <text x={256} y={290}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={170} textAnchor="middle" letterSpacing={-2}
                    stroke={BLACK} strokeWidth={14} paintOrder="stroke fill" fill="url(#cork-grad)"
                    style={{ filter: `drop-shadow(0 8px 0 #000) drop-shadow(0 0 18px ${HIGHLIGHT_GREEN})` }}>
                    CORK
                </text>
            </g>
            <text x={120} y={130}
                fontFamily="'Mukta Mahee', system-ui, sans-serif"
                fontWeight={700} fontSize={22} letterSpacing={4} fill={HIGHLIGHT_GREEN} opacity={0.85}>
                ↻ AXIS
            </text>
        </>
    );
}

// ─── CELEBRITY FLAVOR HEROES ─────────────────────────────────────
// K-Fed, Britney, Ellen — pop-culture-reference flavor tags. Hand-drawn
// silhouette portraits + tabloid-cover energy.

// K-FED — tabloid headshot. Stencil silhouette with goatee + sideways cap +
// "EXCLUSIVE" tabloid banner. The most-used celeb tag (36 uses, 23 users).
function KFed() {
    return (
        <>
            {/* tabloid-pink background */}
            <rect x={48} y={68} width={416} height={376} rx={8}
                fill={NEON_PINK} stroke={BLACK} strokeWidth={6}
                style={{ filter: "drop-shadow(0 10px 0 #000)" }} />
            {/* yellow "EXCLUSIVE" banner */}
            <g transform="translate(60 90) rotate(-8)">
                <rect x={0} y={0} width={170} height={36} fill={BEER_YELLOW} stroke={BLACK} strokeWidth={4} />
                <text x={85} y={26}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={26} textAnchor="middle" letterSpacing={3} fill={BLACK}>
                    EXCLUSIVE!
                </text>
            </g>
            {/* portrait silhouette */}
            <g transform="translate(256 260)" style={{ filter: "drop-shadow(0 6px 0 #000)" }}>
                {/* head */}
                <ellipse cx={0} cy={-12} rx={70} ry={84} fill={WHITE} stroke={BLACK} strokeWidth={5} />
                {/* sideways snapback cap */}
                <path
                    d="M -90 -50
                       Q -90 -100, -10 -100
                       Q 70 -100, 70 -50
                       L 70 -30
                       L -90 -30 Z"
                    fill={BLACK} stroke={BLACK} strokeWidth={4} />
                <rect x={-90} y={-32} width={160} height={10} fill={BLACK} />
                {/* brim, jutting sideways */}
                <path d="M 70 -45 L 130 -25 L 130 -10 L 70 -25 Z" fill={BLACK} stroke={BLACK} strokeWidth={3} />
                {/* shades */}
                <rect x={-50} y={-16} width={40} height={20} rx={4} fill={BLACK} />
                <rect x={10} y={-16} width={40} height={20} rx={4} fill={BLACK} />
                <line x1={-10} y1={-6} x2={10} y2={-6} stroke={BLACK} strokeWidth={3} />
                {/* goatee */}
                <path d="M -8 50 Q 0 76, 8 50 L 4 60 L 0 64 L -4 60 Z" fill={BLACK} />
                <path d="M -16 38 Q 0 30 16 38" stroke={BLACK} strokeWidth={3} fill="none" />
                {/* mouth */}
                <line x1={-12} y1={30} x2={12} y2={30} stroke={BLACK} strokeWidth={3} />
            </g>
            {/* the name */}
            <g transform="translate(256 420)">
                <text x={0} y={0}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={70} textAnchor="middle" letterSpacing={6}
                    stroke={BLACK} strokeWidth={10} paintOrder="stroke fill" fill={BEER_YELLOW}>
                    K-FED
                </text>
            </g>
            {/* small star bursts for tabloid energy */}
            <g fill={BEER_YELLOW} stroke={BLACK} strokeWidth={2}>
                <polygon points="430,110 438,128 458,128 442,140 448,160 430,148 412,160 418,140 402,128 422,128" />
                <polygon points="78,400 82,412 96,412 84,420 88,432 78,424 68,432 72,420 60,412 74,412" />
            </g>
        </>
    );
}

// BRITNEY — pink-on-black, hand-drawn pigtails. (Lower priority than K-Fed
// but earns its slot by association.)
function Britney() {
    return (
        <>
            <rect x={48} y={68} width={416} height={376} rx={20}
                fill={BLACK} stroke={NEON_PINK} strokeWidth={6}
                style={{ filter: `drop-shadow(0 10px 0 #000) drop-shadow(0 0 22px ${NEON_PINK})` }} />
            {/* portrait */}
            <g transform="translate(256 240)">
                <ellipse cx={0} cy={0} rx={64} ry={78} fill={WHITE} stroke={NEON_PINK} strokeWidth={5} />
                {/* pigtails */}
                <ellipse cx={-72} cy={20} rx={26} ry={50} fill={BEER_YELLOW} stroke={BLACK} strokeWidth={4} transform="rotate(-12 -72 20)" />
                <ellipse cx={72} cy={20} rx={26} ry={50} fill={BEER_YELLOW} stroke={BLACK} strokeWidth={4} transform="rotate(12 72 20)" />
                {/* hair top */}
                <path d="M -50 -50 Q 0 -90, 50 -50 L 50 -30 Q 0 -60, -50 -30 Z" fill={BEER_YELLOW} stroke={BLACK} strokeWidth={4} />
                {/* eyes (winking) */}
                <path d="M -28 -2 Q -22 -8, -16 -2" stroke={BLACK} strokeWidth={3} fill="none" />
                <circle cx={22} cy={-2} r={4} fill={BLACK} />
                {/* lips */}
                <path d="M -14 30 Q 0 40, 14 30" stroke={HIGHLIGHT_DANGER} strokeWidth={5} fill="none" strokeLinecap="round" />
            </g>
            <text x={256} y={420}
                fontFamily="Anton, 'Mukta Mahee', sans-serif"
                fontWeight={700} fontSize={72} textAnchor="middle" letterSpacing={6}
                stroke={BLACK} strokeWidth={10} paintOrder="stroke fill" fill={NEON_PINK}
                style={{ filter: `drop-shadow(0 0 14px ${NEON_PINK})` }}>
                BRITNEY
            </text>
        </>
    );
}

// 50-50 — chrome-rail-stamp. Two huge digits balanced on either side of a
// rail glyph, metallic gradient, very "PRO MODEL DECK" energy.
function FiftyFifty() {
    return (
        <>
            <defs>
                <linearGradient id="chrome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fff" />
                    <stop offset="45%" stopColor="#bcd" />
                    <stop offset="55%" stopColor="#789" />
                    <stop offset="100%" stopColor="#fff" />
                </linearGradient>
            </defs>
            {/* the rail itself */}
            <g transform="translate(0 360)" style={{ filter: "drop-shadow(0 6px 0 #000)" }}>
                <rect x={40} y={-10} width={432} height={20} rx={10}
                    fill="url(#chrome)" stroke={BLACK} strokeWidth={4} />
                <rect x={40} y={-10} width={432} height={6} rx={3} fill={WHITE} opacity={0.5} />
                {/* rail uprights */}
                <rect x={88} y={10} width={8} height={36} fill={BLACK} />
                <rect x={416} y={10} width={8} height={36} fill={BLACK} />
            </g>
            {/* the two digit groups */}
            <g transform="rotate(-4 256 220)">
                <text x={142} y={280}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={210} textAnchor="middle" letterSpacing={-4}
                    stroke={BLACK} strokeWidth={14} paintOrder="stroke fill" fill="url(#chrome)"
                    style={{ filter: `drop-shadow(0 8px 0 #000) drop-shadow(0 0 18px ${ELECTRIC_BLUE})` }}>
                    50
                </text>
                <text x={256} y={280}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={210} textAnchor="middle" letterSpacing={-4}
                    fill={ELECTRIC_BLUE} stroke={BLACK} strokeWidth={14} paintOrder="stroke fill">
                    -
                </text>
                <text x={370} y={280}
                    fontFamily="Anton, 'Mukta Mahee', sans-serif"
                    fontWeight={700} fontSize={210} textAnchor="middle" letterSpacing={-4}
                    stroke={BLACK} strokeWidth={14} paintOrder="stroke fill" fill="url(#chrome)"
                    style={{ filter: `drop-shadow(0 8px 0 #000) drop-shadow(0 0 18px ${ELECTRIC_BLUE})` }}>
                    50
                </text>
            </g>
            <text x={256} y={140}
                fontFamily="'Mukta Mahee', system-ui, sans-serif"
                fontWeight={700} fontSize={22} textAnchor="middle" letterSpacing={6} fill={ELECTRIC_BLUE} opacity={0.85}>
                ▤ RAIL
            </text>
        </>
    );
}

// Grab heroes are now typography-only. The previous board+dot iconography
// was incoherent (a rectangle with a dot doesn't communicate "front-hand
// toe-edge"). At sticker size, bold type wins over diagrammatic accuracy.
// Each grab gets a distinct palette + tilt so they read as a family while
// still being individually distinguishable.

function makeGrabHero(label: string, palette: {
    grad0: string;
    grad1: string;
    accent: string;
    tilt: number;
}) {
    const gradId = `grab-grad-${label.toLowerCase().replace(/\s+/g, "-")}`;
    return function GrabHero() {
        return (
            <>
                <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0.4">
                        <stop offset="0%" stopColor={palette.grad0} />
                        <stop offset="100%" stopColor={palette.grad1} />
                    </linearGradient>
                </defs>
                {/* Background depth: oversized faded text behind, slightly offset.
                    Creates a "stadium poster" sense of weight without diagrammatic
                    iconography. */}
                <g transform={`translate(256 256) rotate(${palette.tilt})`}>
                    <text x={0} y={28}
                        fontFamily="Anton, 'Mukta Mahee', sans-serif"
                        fontWeight={700} textAnchor="middle" letterSpacing={-2}
                        fontSize={fitFontSize(label, 360, 200)}
                        fill={palette.grad1} opacity={0.18}>
                        {label.toUpperCase()}
                    </text>
                </g>
                {/* Main label */}
                <g transform={`translate(256 256) rotate(${palette.tilt})`}>
                    <text x={0} y={24}
                        fontFamily="Anton, 'Mukta Mahee', sans-serif"
                        fontWeight={700} textAnchor="middle" letterSpacing={-2}
                        fontSize={fitFontSize(label, 360, 200)}
                        stroke={BLACK} strokeWidth={14} paintOrder="stroke fill"
                        fill={`url(#${gradId})`}
                        style={{ filter: `drop-shadow(0 8px 0 #000) drop-shadow(0 0 18px ${palette.accent})` }}>
                        {label.toUpperCase()}
                    </text>
                </g>
                {/* GRAB eyebrow — fixed position, doesn't compete with main label */}
                <g transform="translate(0 140)">
                    <line x1={170} y1={-4} x2={210} y2={-4} stroke={palette.accent} strokeWidth={3} opacity={0.85} />
                    <text x={220} y={0}
                        fontFamily="'Mukta Mahee', system-ui, sans-serif"
                        fontWeight={700} fontSize={22} letterSpacing={6}
                        fill={palette.accent} opacity={0.9}>
                        GRAB
                    </text>
                    <line x1={272} y1={-4} x2={312} y2={-4} stroke={palette.accent} strokeWidth={3} opacity={0.85} />
                </g>
            </>
        );
    };
}

// Naive but effective auto-fit: shrink font until label width fits the box.
// Anton glyphs average ~0.42em advance; subtract for letter-spacing.
function fitFontSize(label: string, maxWidth: number, maxSize: number): number {
    const advance = 0.42;       // Anton avg glyph width as fraction of fontSize
    const len = label.length;
    if (len === 0) return maxSize;
    const fit = maxWidth / (len * advance);
    return Math.min(maxSize, Math.floor(fit));
}

// Each grab has its own palette / tilt for variety. Top three grabs by usage:
// safety (38), mute (12), indy (next common). Tail/Nose use the same engine.
const GrabSafety = makeGrabHero("Safety", {
    grad0: HIGHLIGHT_GREEN, grad1: ELECTRIC_BLUE, accent: HIGHLIGHT_GREEN, tilt: -4,
});
const GrabMute = makeGrabHero("Mute", {
    grad0: WHITE,           grad1: NEON_PINK,     accent: NEON_PINK,       tilt: -6,
});
const GrabIndy = makeGrabHero("Indy", {
    grad0: ELECTRIC_BLUE,   grad1: NEON_PINK,     accent: ELECTRIC_BLUE,   tilt: -2,
});
const GrabTail = makeGrabHero("Tail", {
    grad0: NEON_PINK,       grad1: HIGHLIGHT_DANGER, accent: NEON_PINK,    tilt: 3,
});
const GrabNose = makeGrabHero("Nose", {
    grad0: ELECTRIC_BLUE,   grad1: HIGHLIGHT_GREEN, accent: ELECTRIC_BLUE, tilt: -5,
});
const GrabJapan = makeGrabHero("Japan", {
    grad0: HIGHLIGHT_DANGER, grad1: WHITE,        accent: HIGHLIGHT_DANGER, tilt: -3,
});

// ═══════════════════════════════════════════════════════════════════
// Public registry
// ═══════════════════════════════════════════════════════════════════

// Wrap a render function as a StickerDef. `label` (optional) is the human tag
// text used by the picker's search box.
function makeDef(id: string, render: () => React.ReactNode, label?: string): StickerDef {
    return {
        id,
        label,
        family: "Generic",
        variant: "hero" as never,
        render,
    };
}

// Tag-specific hand-tuned overrides. Keys MUST be lowercase.
// Ordering / inclusion reflects the live usage data from
// public.video_components_tags (top tags by COUNT(*) and unique users).
export const CONCEPT_STICKERS: Record<string, StickerDef[]> = {
    // Top spins — 360 is #1 by a mile (129 uses, 67 users)
    "360":          [makeDef("hero:360", Degree360)],
    "180":          [makeDef("hero:180", Degree180)],
    "270":          [makeDef("hero:270", Degree270)],
    "540":          [makeDef("hero:540", Degree540)],
    "720":          [makeDef("hero:720", Degree720)],
    "900":          [makeDef("hero:900", Degree900)],
    "1080":         [makeDef("hero:1080", Degree1080)],

    // Top axis tricks
    "cork":         [makeDef("hero:cork", AxisCork)],

    // Top rail trick
    "50-50":        [makeDef("hero:50-50", FiftyFifty)],

    // Grabs — typography-only heroes, one per grab name. Same engine, distinct palette.
    "safety":       [makeDef("hero:safety", GrabSafety)],
    "mute":         [makeDef("hero:mute", GrabMute)],
    "indy":         [makeDef("hero:indy", GrabIndy)],
    "tail":         [makeDef("hero:tail", GrabTail)],
    "nose":         [makeDef("hero:nose", GrabNose)],
    "japan":        [makeDef("hero:japan", GrabJapan)],

    // Flavor / culture — these are the viral candidates
    "beer in hand": [makeDef("hero:beer-in-hand", BeerInHand)],
    "hall of meat": [makeDef("hero:hall-of-meat", HallOfMeat)],
    "hand drag":    [makeDef("hero:hand-drag", HandDrag)],
    "parkasaurus":  [makeDef("hero:parkasaurus", Parkasaurus)],
    "k-fed":        [makeDef("hero:k-fed", KFed)],
    "britney":      [makeDef("hero:britney", Britney)],
};

// Moment stickers — always available, surfaced as a "Hype" row in the tray.
export function buildMomentStickers(username: string): StickerDef[] {
    return [
        makeDef("moment:just-landed", JustLanded, "Just Landed"),
        makeDef("moment:tow-rope-pass", () => TowRopePass(username), "Tow Rope Pass"),
    ];
}

// Flavor / culture stickers — hand-tuned, fun, and not tied to a specific trick
// mechanic. Surfaced in an always-available "Flavor" row so a rider can slap one
// on any clip (they're ALSO wired as tag heroes in CONCEPT_STICKERS, so a clip
// actually tagged e.g. "beer in hand" still gets it in its tag row). Distinct
// "flavor:" ids keep them separable from the tag-driven copies.
export function buildFlavorStickers(): StickerDef[] {
    return [
        makeDef("flavor:beer-in-hand", BeerInHand, "Beer in Hand"),
        makeDef("flavor:parkasaurus", Parkasaurus, "Parkasaurus"),
        makeDef("flavor:hall-of-meat", HallOfMeat, "Hall of Meat"),
        makeDef("flavor:hand-drag", HandDrag, "Hand Drag"),
        makeDef("flavor:k-fed", KFed, "K-Fed"),
        makeDef("flavor:britney", Britney, "Britney"),
    ];
}

// The natural ViewBox of every hero — exported so the editor renders at the
// same size as the default variants.
export const HERO_VIEWBOX = VB;
